// src/components/then-and-now/ThenAndNowCard.tsx
//
// Phase 5E — renders a [THEN_AND_NOW] block. Two Wikipedia-
// resolved images side-by-side (or stacked on narrow viewports)
// showing the same subject across time. Heads off the most
// common heritage-AI failure mode: the AI describes layered
// change in prose, the user can't picture it.
//
// Layout choice: side-by-side at ≥sm breakpoint, stacked at xs.
// The stacked variant on phones keeps each image tall enough to
// read; a tiny 2-up grid on a 380px viewport collapses both
// images to ~180px wide, useless.
//
// Degraded states:
//   - Both images resolve: full card with subject heading,
//     two-up grid, captions + years.
//   - One image fails: shows the resolving side as a single-image
//     card with the era label. Falls back gracefully — better
//     than rendering nothing when half the data is good.
//   - Both fail: renders nothing. The AI's prose already
//     described the contrast.
//
// Interaction: tap either image → lightbox with full-resolution
// fetch + caption. Uses the existing useEntityImage hook so
// images cache across renders (no re-fetch on re-render).

import React, { useState } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { useEntityImage } from '../../hooks/useEntityImage';
import { useT } from '../../i18n/useT';
import type { ThenAndNowBlock, ThenAndNowSide } from '../../utils/interactiveContent';

interface Props {
  block: ThenAndNowBlock;
}

interface SideViewProps {
  side: ThenAndNowSide;
  /** Tag shown overlaid on the image — 'Then' or 'Now'. */
  tag: string;
  onClick: () => void;
}

const SideView: React.FC<SideViewProps> = ({ side, tag, onClick }) => {
  const theme = useTheme();
  const image = useEntityImage('place', side.wikipedia_title);

  if (!image) return null;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'block',
        textAlign: 'left',
        cursor: 'zoom-in',
        bgcolor: 'action.hover',
        transition: 'transform 0.05s ease, box-shadow 0.15s ease',
        '&:active': { transform: 'scale(0.997)' },
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <Box
        component="img"
        src={image.thumbnailUrl}
        alt={`${tag}: ${side.caption}`}
        loading="lazy"
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: '4 / 3',
          objectFit: 'cover',
        }}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
      {/* Tag pill — "Then" / "Now". Sits in the top-left of the
          image, semi-transparent dark bg + light text so it
          works against any image content. */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: 'rgba(0,0,0,0.65)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          backdropFilter: 'blur(4px)',
        }}
      >
        {tag} · {side.year}
      </Box>
      <Box sx={{ px: 1.25, py: 1, bgcolor: 'background.paper' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            lineHeight: 1.4,
            // Allow up to 3 lines, then ellipsize
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {side.caption}
        </Typography>
      </Box>
      {/* Border ring on the whole side card */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          pointerEvents: 'none',
        }}
      />
    </ButtonBase>
  );
};

export const ThenAndNowCard: React.FC<Props> = ({ block }) => {
  const t = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [lightbox, setLightbox] = useState<ThenAndNowSide | null>(null);

  // We can't know up-front whether images will resolve — the
  // useEntityImage hook is async with module-level cache. We
  // render the SideView components and let them no-op if the
  // image is missing. To avoid showing the "subject" heading on
  // a card where BOTH sides will be empty, we'd need to await
  // both lookups. Instead we accept that for the brief moment
  // both are loading, the heading shows alone. Acceptable.

  return (
    <>
      <Box
        sx={{
          my: 2,
          p: 1.75,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mb: 1.25,
            color: 'text.primary',
          }}
        >
          {block.subject}{' '}
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontWeight: 400 }}
          >
            · {t('then & now')}
          </Box>
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 1.25,
          }}
        >
          <SideView
            side={block.then}
            tag={t('Then')}
            onClick={() => setLightbox(block.then)}
          />
          <SideView
            side={block.now}
            tag={t('Now')}
            onClick={() => setLightbox(block.now)}
          />
        </Box>
      </Box>

      {/* Lightbox — full-resolution image + caption + open-on-
          Wikipedia link. Tap-outside or close X dismisses. */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
            borderRadius: isMobile ? 0 : 2,
            overflow: 'hidden',
          },
        }}
      >
        {lightbox && <LightboxContent side={lightbox} subject={block.subject} onClose={() => setLightbox(null)} />}
      </Dialog>
    </>
  );
};

/** Lightbox body — needs its own component so the useEntityImage
 *  hook can fetch the full-resolution URL. */
const LightboxContent: React.FC<{
  side: ThenAndNowSide;
  subject: string;
  onClose: () => void;
}> = ({ side, subject, onClose }) => {
  const t = useT();
  const theme = useTheme();
  const image = useEntityImage('place', side.wikipedia_title);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subject} — {side.year}
          </Typography>
        </Box>
        {image?.articleUrl && (
          <IconButton
            size="small"
            component="a"
            href={image.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('Open on Wikipedia')}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose} aria-label={t('Close')} size="small">
          <Close />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
          minHeight: 280,
          p: 1,
        }}
      >
        {image && (
          <Box
            component="img"
            src={image.fullUrl}
            alt={side.caption}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {side.caption}
        </Typography>
      </Box>
    </Box>
  );
};
